import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Download, FileSpreadsheet, Clock, Trash2, AlertCircle, CheckCircle, ChevronDown, ChevronUp, Info, HelpCircle, TrendingUp, TrendingDown, Minus, BarChart3, GraduationCap, LineChart, Calendar, ArrowLeftRight, ArrowRight, FileText, Sparkles, Loader2, Gauge } from 'lucide-react';
import DriverImportPanel from '../components/performance/DriverImportPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart, BarChart, Bar, Cell } from 'recharts';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

// Pillar colors for chart
const PILLAR_COLORS = {
  'P1 Access & Engagement': '#0d9488',
  'P2 Quality & Effectiveness': '#6366f1',
  'P3 Skills & Capability': '#f59e0b',
  'P4 Efficiency & ROI': '#ec4899'
};

const MEASURE_COLORS = [
  '#0d9488', '#6366f1', '#f59e0b', '#ec4899', '#3b82f6',
  '#10b981', '#8b5cf6', '#f97316', '#ef4444', '#06b6d4',
  '#84cc16', '#a855f7', '#14b8a6', '#f43f5e', '#22d3ee'
];

export default function DataManagementPage() {
  const { user, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(['kpi', 'drivers'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'lei');
  const focusCohortId = searchParams.get('cohort') || '';
  const focusCohortName = searchParams.get('cohort_name') || '';
  
  // Organization filter (Super Admin)
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  
  const navigate = useNavigate();
  // LEI Import State
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
  const [isTrendsExpanded, setIsTrendsExpanded] = useState(true);
  const [componentDefs, setComponentDefs] = useState({});
  const [trendsData, setTrendsData] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState('all');
  const [selectedMeasure, setSelectedMeasure] = useState('all');
  const [leiDateRange, setLeiDateRange] = useState([0, 100]); // Percentage-based range slider
  
  // KPI Import State
  const [isKpiUploading, setIsKpiUploading] = useState(false);
  const [kpiSource, setKpiSource] = useState('');
  const [kpiUploadResult, setKpiUploadResult] = useState(null);
  const [kpiHistory, setKpiHistory] = useState([]);
  const [isKpiHistoryExpanded, setIsKpiHistoryExpanded] = useState(true);
  const [isKpiTrendsExpanded, setIsKpiTrendsExpanded] = useState(true);
  const [kpiTrendsData, setKpiTrendsData] = useState(null);
  const [kpiComponentDefs, setKpiComponentDefs] = useState({});
  const [selectedKpi, setSelectedKpi] = useState('all');
  const [kpiDateRange, setKpiDateRange] = useState([0, 100]); // Percentage-based range slider
  
  // Compare Periods State - LEI
  const [isLeiCompareExpanded, setIsLeiCompareExpanded] = useState(false);
  const [leiPeriodA, setLeiPeriodA] = useState('q1'); // quarter presets
  const [leiPeriodB, setLeiPeriodB] = useState('q2');
  
  // Compare Periods State - KPI
  const [isKpiCompareExpanded, setIsKpiCompareExpanded] = useState(false);
  const [kpiPeriodA, setKpiPeriodA] = useState('q1');
  const [kpiPeriodB, setKpiPeriodB] = useState('q2');
  
  // Export state
  const [isExportingLei, setIsExportingLei] = useState(false);
  const [isExportingKpi, setIsExportingKpi] = useState(false);
  
  // AI Insights state
  const [leiAiInsights, setLeiAiInsights] = useState(null);
  const [kpiAiInsights, setKpiAiInsights] = useState(null);
  const [isGeneratingLeiInsights, setIsGeneratingLeiInsights] = useState(false);
  const [isGeneratingKpiInsights, setIsGeneratingKpiInsights] = useState(false);
  
  const fileInputRef = useRef(null);
  const kpiFileInputRef = useRef(null);
  
  // Define all fetch functions BEFORE the useEffect that uses them
  const fetchImportHistory = useCallback(async () => {
    try {
      const params = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const response = await api.get(`/import/data/lei-baselines/history${params}`);
      setImportHistory(response.data.records || []);
    } catch (error) {
      logger.error('Failed to fetch import history:', error);
    }
  }, [selectedOrg]);
  
  const fetchComponentDefinitions = useCallback(async () => {
    try {
      const response = await api.get('/import/data/component-definitions');
      setComponentDefs(response.data || {});
    } catch (error) {
      logger.error('Failed to fetch component definitions:', error);
    }
  }, []);
  
  const fetchTrendsData = useCallback(async () => {
    try {
      const params = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const response = await api.get(`/import/data/lei-baselines/trends${params}`);
      setTrendsData(response.data);
    } catch (error) {
      logger.error('Failed to fetch trends data:', error);
    }
  }, [selectedOrg]);
  
  // KPI Fetch Functions
  const fetchKpiHistory = useCallback(async () => {
    try {
      const params = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const response = await api.get(`/import/data/kpi-actuals/history${params}`);
      setKpiHistory(response.data.records || []);
    } catch (error) {
      logger.error('Failed to fetch KPI history:', error);
    }
  }, [selectedOrg]);
  
  const fetchKpiTrendsData = useCallback(async () => {
    try {
      const params = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const response = await api.get(`/import/data/kpi-actuals/trends${params}`);
      setKpiTrendsData(response.data);
    } catch (error) {
      logger.error('Failed to fetch KPI trends data:', error);
    }
  }, [selectedOrg]);
  
  const fetchKpiComponentDefinitions = useCallback(async () => {
    try {
      const response = await api.get('/import/data/kpi-actuals/component-definitions');
      setKpiComponentDefs(response.data || {});
    } catch (error) {
      logger.error('Failed to fetch KPI component definitions:', error);
    }
  }, []);
  
  // Now the useEffect can safely reference the fetch functions
  useEffect(() => {
    fetchImportHistory();
    fetchComponentDefinitions();
    fetchTrendsData();
    fetchKpiHistory();
    fetchKpiTrendsData();
    fetchKpiComponentDefinitions();
  }, [fetchImportHistory, fetchComponentDefinitions, fetchTrendsData, fetchKpiHistory, fetchKpiTrendsData, fetchKpiComponentDefinitions]);
  
  // Fetch organizations for Super Admin filter
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOrgs = async () => {
      try {
        const res = await api.get('/rbac/organizations');
        setOrganizations(res.data.organizations || []);
      } catch (e) { logger.error('Error fetching orgs:', e); }
    };
    fetchOrgs();
  }, [isSuperAdmin]);
  
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/import/data/templates/lei-baselines?format=xlsx', { 
        responseType: 'blob' 
      });
      
      // Check if response is actually JSON error (not a blob)
      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        // It's an error response
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Failed to download template');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'lei_baselines_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully!');
    } catch (error) {
      logger.error('Download error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to download template';
      toast.error(errorMessage);
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/import/data/lei-baselines', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(response.data);
      
      if (response.data.success) {
        if (response.data.error_count > 0) {
          toast.warning(`Imported ${response.data.imported_count} records with ${response.data.error_count} errors`);
        } else {
          toast.success(`Successfully imported ${response.data.imported_count} records!`);
        }
        fetchImportHistory();
        fetchTrendsData();
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to import file: ' + (error.response?.data?.detail || error.message));
      setUploadResult({
        success: false,
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleDeleteImport = async (importId) => {
    if (!confirm('Are you sure you want to delete this import batch?')) return;
    
    try {
      await api.delete(`/import/data/lei-baselines/${importId}`);
      toast.success('Import batch deleted');
      fetchImportHistory();
      fetchTrendsData();
    } catch (error) {
      toast.error('Failed to delete import batch');
    }
  };
  
  // KPI Download Template
  const handleDownloadKpiTemplate = async () => {
    try {
      const response = await api.get('/import/data/templates/kpi-actuals?format=xlsx', { 
        responseType: 'blob' 
      });
      
      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Failed to download template');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kpi_actuals_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('KPI template downloaded successfully!');
    } catch (error) {
      logger.error('Download error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to download template';
      toast.error(errorMessage);
    }
  };
  
  // KPI File Upload
  const handleKpiFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsKpiUploading(true);
    setKpiUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/import/data/kpi-actuals${kpiSource.trim() ? `?source=${encodeURIComponent(kpiSource.trim())}` : ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setKpiUploadResult(response.data);
      
      if (response.data.success) {
        if (response.data.errors?.length > 0) {
          toast.warning(`Imported ${response.data.imported_count} KPI records with some warnings`);
        } else {
          toast.success(`Successfully imported ${response.data.imported_count} KPI records!`);
        }
        fetchKpiHistory();
        fetchKpiTrendsData();
      }
    } catch (error) {
      logger.error('KPI upload error:', error);
      toast.error('Failed to import KPI file: ' + (error.response?.data?.detail || error.message));
      setKpiUploadResult({
        success: false,
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setIsKpiUploading(false);
      if (kpiFileInputRef.current) {
        kpiFileInputRef.current.value = '';
      }
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';  // Return empty string for invalid dates
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  };

  // Export period comparison as PDF or Excel
  const exportPeriodComparison = async (dataType, periodAMetrics, periodBMetrics, format = 'pdf') => {
    const setExporting = dataType === 'lei' ? setIsExportingLei : setIsExportingKpi;
    setExporting(true);
    
    try {
      const metricsKey = dataType === 'lei' ? 'measures' : 'kpis';
      
      const payload = {
        data_type: dataType,
        period_a: {
          label: periodAMetrics?.periodLabel || 'Period A',
          startDate: periodAMetrics?.startDate || '',
          endDate: periodAMetrics?.endDate || '',
          metrics: periodAMetrics?.[metricsKey] || {}
        },
        period_b: {
          label: periodBMetrics?.periodLabel || 'Period B',
          startDate: periodBMetrics?.startDate || '',
          endDate: periodBMetrics?.endDate || '',
          metrics: periodBMetrics?.[metricsKey] || {}
        },
        export_format: format
      };
      
      const response = await api.post('/import/period-comparison/export', payload, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '_');
      link.download = `Period_Comparison_${dataType.toUpperCase()}_${timestamp}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Period comparison exported as ${format.toUpperCase()}`);
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Failed to export period comparison: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  // Generate AI insights for period comparison
  const generateAiInsights = async (dataType, periodAMetrics, periodBMetrics) => {
    const setGenerating = dataType === 'lei' ? setIsGeneratingLeiInsights : setIsGeneratingKpiInsights;
    const setInsights = dataType === 'lei' ? setLeiAiInsights : setKpiAiInsights;
    
    setGenerating(true);
    
    try {
      const metricsKey = dataType === 'lei' ? 'measures' : 'kpis';
      
      const payload = {
        data_type: dataType,
        period_a: {
          label: periodAMetrics?.periodLabel || 'Period A',
          startDate: periodAMetrics?.startDate || '',
          endDate: periodAMetrics?.endDate || '',
          metrics: periodAMetrics?.[metricsKey] || {}
        },
        period_b: {
          label: periodBMetrics?.periodLabel || 'Period B',
          startDate: periodBMetrics?.startDate || '',
          endDate: periodBMetrics?.endDate || '',
          metrics: periodBMetrics?.[metricsKey] || {}
        }
      };
      
      const response = await api.post('/import/period-comparison/generate-insights', payload);
      
      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }
      
      setInsights(response.data);
      toast.success('AI insights generated successfully!');
    } catch (error) {
      logger.error('AI insights error:', error);
      toast.error('Failed to generate AI insights: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  // Export with AI insights
  const exportWithAiInsights = async (dataType, periodAMetrics, periodBMetrics, format, aiInsights) => {
    const setExporting = dataType === 'lei' ? setIsExportingLei : setIsExportingKpi;
    setExporting(true);
    
    try {
      const metricsKey = dataType === 'lei' ? 'measures' : 'kpis';
      
      const payload = {
        data_type: dataType,
        period_a: {
          label: periodAMetrics?.periodLabel || 'Period A',
          startDate: periodAMetrics?.startDate || '',
          endDate: periodAMetrics?.endDate || '',
          metrics: periodAMetrics?.[metricsKey] || {}
        },
        period_b: {
          label: periodBMetrics?.periodLabel || 'Period B',
          startDate: periodBMetrics?.startDate || '',
          endDate: periodBMetrics?.endDate || '',
          metrics: periodBMetrics?.[metricsKey] || {}
        },
        export_format: format,
        include_ai_insights: true,
        ai_insights: aiInsights?.insights || ''
      };
      
      const response = await api.post('/import/period-comparison/export', payload, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '_');
      link.download = `Period_Comparison_${dataType.toUpperCase()}_with_AI_${timestamp}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Period comparison with AI insights exported as ${format.toUpperCase()}`);
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Failed to export: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };
  
  // Group history by import_id
  const groupedHistory = importHistory.reduce((acc, record) => {
    const id = record.import_id || 'unknown';
    if (!acc[id]) {
      acc[id] = {
        import_id: id,
        import_timestamp: record.import_timestamp,
        records: []
      };
    }
    acc[id].records.push(record);
    return acc;
  }, {});
  
  // Sort by import timestamp descending
  const sortedBatches = Object.values(groupedHistory).sort((a, b) => 
    new Date(b.import_timestamp) - new Date(a.import_timestamp)
  );

  // Helper to check if a date string is valid
  const isValidDate = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    return !isNaN(date.getTime()) && date.getFullYear() >= 2000;
  };

  // Get all unique dates from LEI trends for the slider (filter out invalid dates)
  const allLeiDates = useMemo(() => {
    if (!trendsData?.trends) return [];
    const dateSet = new Set();
    Object.values(trendsData.trends).forEach(measureData => {
      measureData.data_points.forEach(dp => {
        const date = dp.measurement_date?.split('T')[0] || dp.measurement_date;
        if (date && isValidDate(date)) dateSet.add(date);
      });
    });
    return Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));
  }, [trendsData]);

  // Get all unique dates from KPI trends for the slider (filter out invalid dates)
  const allKpiDates = useMemo(() => {
    if (!kpiTrendsData?.by_kpi) return [];
    const dateSet = new Set();
    Object.values(kpiTrendsData.by_kpi).forEach(kpiData => {
      kpiData.data_points.forEach(dp => {
        const date = dp.date?.split('T')[0] || dp.date;
        if (date && isValidDate(date)) dateSet.add(date);
      });
    });
    return Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));
  }, [kpiTrendsData]);

  // Helper to get date from slider percentage
  const getDateFromSliderValue = (dates, percentage) => {
    if (dates.length === 0) return null;
    const index = Math.round((percentage / 100) * (dates.length - 1));
    return dates[Math.min(Math.max(index, 0), dates.length - 1)];
  };

  // Get current LEI date range labels
  const leiStartDate = getDateFromSliderValue(allLeiDates, leiDateRange[0]);
  const leiEndDate = getDateFromSliderValue(allLeiDates, leiDateRange[1]);
  
  // Get current KPI date range labels
  const kpiStartDate = getDateFromSliderValue(allKpiDates, kpiDateRange[0]);
  const kpiEndDate = getDateFromSliderValue(allKpiDates, kpiDateRange[1]);

  // Helper to calculate date range preset values
  const getPresetDateRange = (dates, preset) => {
    if (dates.length === 0) return [0, 100];
    
    const endDate = new Date(dates[dates.length - 1]);
    let startDate;
    
    switch (preset) {
      case '3m':
        startDate = new Date(endDate);
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case '6m':
        startDate = new Date(endDate);
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case 'ytd':
        startDate = new Date(endDate.getFullYear(), 0, 1); // Jan 1 of current year
        break;
      case '1y':
        startDate = new Date(endDate);
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      case 'all':
      default:
        return [0, 100];
    }
    
    // Find the index of the closest date to startDate
    let startIndex = 0;
    for (let i = 0; i < dates.length; i++) {
      if (new Date(dates[i]) >= startDate) {
        startIndex = i;
        break;
      }
    }
    
    // Convert to percentage
    const startPercent = Math.round((startIndex / (dates.length - 1)) * 100);
    return [startPercent, 100];
  };

  // Check if a preset is currently active
  const isPresetActive = (dates, currentRange, preset) => {
    const presetRange = getPresetDateRange(dates, preset);
    return currentRange[0] === presetRange[0] && currentRange[1] === presetRange[1];
  };

  // Period comparison presets
  const PERIOD_PRESETS = useMemo(() => [
    { key: 'q1', label: 'Q1', getRange: (year) => ({ start: `${year}-01-01`, end: `${year}-03-31` }) },
    { key: 'q2', label: 'Q2', getRange: (year) => ({ start: `${year}-04-01`, end: `${year}-06-30` }) },
    { key: 'q3', label: 'Q3', getRange: (year) => ({ start: `${year}-07-01`, end: `${year}-09-30` }) },
    { key: 'q4', label: 'Q4', getRange: (year) => ({ start: `${year}-10-01`, end: `${year}-12-31` }) },
    { key: 'h1', label: 'H1', getRange: (year) => ({ start: `${year}-01-01`, end: `${year}-06-30` }) },
    { key: 'h2', label: 'H2', getRange: (year) => ({ start: `${year}-07-01`, end: `${year}-12-31` }) },
    { key: 'last3m', label: 'Last 3M', getRange: (year, refDate) => {
      const end = new Date(refDate);
      const start = new Date(refDate);
      start.setMonth(start.getMonth() - 3);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
    { key: 'prev3m', label: 'Prev 3M', getRange: (year, refDate) => {
      const end = new Date(refDate);
      end.setMonth(end.getMonth() - 3);
      const start = new Date(end);
      start.setMonth(start.getMonth() - 3);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }}
  ], []);

  // Get available years from dates
  const getAvailableYears = (dates) => {
    if (!dates || dates.length === 0) return [];
    const years = new Set();
    dates.forEach(d => years.add(new Date(d).getFullYear()));
    return Array.from(years).sort((a, b) => b - a);
  };

  // Calculate metrics for a period
  const calculatePeriodMetrics = useCallback((trendsData, periodKey, dates) => {
    if (!trendsData?.trends || !dates || dates.length === 0) return null;
    
    const latestDate = dates[dates.length - 1];
    const latestYear = new Date(latestDate).getFullYear();
    const preset = PERIOD_PRESETS.find(p => p.key === periodKey);
    if (!preset) return null;
    
    const { start, end } = preset.getRange(latestYear, latestDate);
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const metrics = {};
    let totalAvg = 0;
    let measureCount = 0;
    
    Object.entries(trendsData.trends).forEach(([measureId, measureData]) => {
      const pointsInPeriod = measureData.data_points.filter(dp => {
        const date = new Date(dp.measurement_date?.split('T')[0] || dp.measurement_date);
        return date >= startDate && date <= endDate;
      });
      
      if (pointsInPeriod.length > 0) {
        const scores = pointsInPeriod.map(p => p.calculated_score);
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        const latest = scores[scores.length - 1];
        const first = scores[0];
        
        metrics[measureId] = {
          name: measureData.measure_name,
          pillar: measureData.pillar,
          avg: avg,
          latest: latest,
          first: first,
          change: latest - first,
          dataPoints: pointsInPeriod.length
        };
        
        totalAvg += avg;
        measureCount++;
      }
    });
    
    return {
      periodLabel: preset.label,
      startDate: start,
      endDate: end,
      measures: metrics,
      overallAvg: measureCount > 0 ? totalAvg / measureCount : 0,
      measureCount
    };
  }, [PERIOD_PRESETS]);

  // Calculate KPI metrics for a period
  const calculateKpiPeriodMetrics = useCallback((kpiTrendsData, periodKey, dates) => {
    if (!kpiTrendsData?.by_kpi || !dates || dates.length === 0) return null;
    
    const latestDate = dates[dates.length - 1];
    const latestYear = new Date(latestDate).getFullYear();
    const preset = PERIOD_PRESETS.find(p => p.key === periodKey);
    if (!preset) return null;
    
    const { start, end } = preset.getRange(latestYear, latestDate);
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const metrics = {};
    
    Object.entries(kpiTrendsData.by_kpi).forEach(([kpiId, kpiData]) => {
      const pointsInPeriod = kpiData.data_points.filter(dp => {
        const date = new Date(dp.date?.split('T')[0] || dp.date);
        return date >= startDate && date <= endDate;
      });
      
      if (pointsInPeriod.length > 0) {
        const values = pointsInPeriod.map(p => p.value);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        const latest = values[values.length - 1];
        const first = values[0];
        
        metrics[kpiId] = {
          name: kpiData.kpi_name,
          unit: kpiData.unit,
          inverse: kpiData.inverse,
          avg: avg,
          latest: latest,
          first: first,
          change: latest - first,
          changePct: first !== 0 ? ((latest - first) / first) * 100 : 0,
          dataPoints: pointsInPeriod.length
        };
      }
    });
    
    return {
      periodLabel: preset.label,
      startDate: start,
      endDate: end,
      kpis: metrics,
      kpiCount: Object.keys(metrics).length
    };
  }, [PERIOD_PRESETS]);

  // Memoized period comparisons
  const leiPeriodAMetrics = useMemo(() => 
    calculatePeriodMetrics(trendsData, leiPeriodA, allLeiDates), 
    [calculatePeriodMetrics, trendsData, leiPeriodA, allLeiDates]
  );
  
  const leiPeriodBMetrics = useMemo(() => 
    calculatePeriodMetrics(trendsData, leiPeriodB, allLeiDates), 
    [calculatePeriodMetrics, trendsData, leiPeriodB, allLeiDates]
  );

  const kpiPeriodAMetrics = useMemo(() => 
    calculateKpiPeriodMetrics(kpiTrendsData, kpiPeriodA, allKpiDates), 
    [calculateKpiPeriodMetrics, kpiTrendsData, kpiPeriodA, allKpiDates]
  );
  
  const kpiPeriodBMetrics = useMemo(() => 
    calculateKpiPeriodMetrics(kpiTrendsData, kpiPeriodB, allKpiDates), 
    [calculateKpiPeriodMetrics, kpiTrendsData, kpiPeriodB, allKpiDates]
  );

  // Process trends data for chart with date range filtering
  const getChartData = () => {
    if (!trendsData?.trends) return [];
    
    // Get all unique dates across all measures
    const dateMap = {};
    
    Object.entries(trendsData.trends).forEach(([measureId, measureData]) => {
      // Filter by pillar if selected
      if (selectedPillar !== 'all' && measureData.pillar !== selectedPillar) return;
      // Filter by measure if selected
      if (selectedMeasure !== 'all' && measureId !== selectedMeasure) return;
      
      measureData.data_points.forEach(dp => {
        const date = dp.measurement_date?.split('T')[0] || dp.measurement_date;
        
        // Skip invalid dates
        if (!date || !isValidDate(date)) return;
        
        // Apply date range filter
        if (leiStartDate && leiEndDate) {
          const dpDate = new Date(date);
          const startDate = new Date(leiStartDate);
          const endDate = new Date(leiEndDate);
          if (dpDate < startDate || dpDate > endDate) return;
        }
        
        if (!dateMap[date]) {
          dateMap[date] = { date };
        }
        dateMap[date][measureId] = dp.calculated_score;
      });
    });
    
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getVisibleMeasures = () => {
    if (!trendsData?.trends) return [];
    
    return Object.entries(trendsData.trends)
      .filter(([measureId, measureData]) => {
        if (selectedPillar !== 'all' && measureData.pillar !== selectedPillar) return false;
        if (selectedMeasure !== 'all' && measureId !== selectedMeasure) return false;
        return true;
      })
      .map(([measureId, measureData], index) => ({
        measureId,
        name: measureData.measure_name,
        pillar: measureData.pillar,
        color: MEASURE_COLORS[index % MEASURE_COLORS.length],
        trend: measureData.trend
      }));
  };

  const getPillars = () => {
    if (!trendsData?.trends) return [];
    const pillars = new Set();
    Object.values(trendsData.trends).forEach(m => {
      if (m.pillar) pillars.add(m.pillar);
    });
    return Array.from(pillars).sort();
  };

  const getMeasures = () => {
    if (!trendsData?.trends) return [];
    return Object.entries(trendsData.trends)
      .filter(([_, m]) => selectedPillar === 'all' || m.pillar === selectedPillar)
      .map(([id, m]) => ({ id, name: m.measure_name, pillar: m.pillar }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const chartData = getChartData();
  const visibleMeasures = getVisibleMeasures();
  const hasTrendData = trendsData?.total_data_points > 0;

  // KPI chart data processing with date range filtering
  const getKpiChartData = () => {
    if (!kpiTrendsData?.by_kpi) return [];
    
    const dateMap = {};
    
    Object.entries(kpiTrendsData.by_kpi).forEach(([kpiId, kpiData]) => {
      if (selectedKpi !== 'all' && kpiId !== selectedKpi) return;
      
      kpiData.data_points.forEach(dp => {
        const date = dp.date?.split('T')[0] || dp.date;
        
        // Skip invalid dates
        if (!date || !isValidDate(date)) return;
        
        // Apply date range filter
        if (kpiStartDate && kpiEndDate) {
          const dpDate = new Date(date);
          const startDate = new Date(kpiStartDate);
          const endDate = new Date(kpiEndDate);
          if (dpDate < startDate || dpDate > endDate) return;
        }
        
        if (!dateMap[date]) {
          dateMap[date] = { date };
        }
        dateMap[date][kpiId] = dp.value;
      });
    });
    
    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getVisibleKpis = () => {
    if (!kpiTrendsData?.by_kpi) return [];
    
    return Object.entries(kpiTrendsData.by_kpi)
      .filter(([kpiId]) => selectedKpi === 'all' || kpiId === selectedKpi)
      .map(([kpiId, kpiData], index) => ({
        kpiId,
        name: kpiData.kpi_name,
        unit: kpiData.unit,
        color: MEASURE_COLORS[index % MEASURE_COLORS.length],
        inverse: kpiData.inverse
      }));
  };

  const getKpiList = () => {
    if (!kpiTrendsData?.by_kpi) return [];
    return Object.entries(kpiTrendsData.by_kpi)
      .map(([id, data]) => ({ id, name: data.kpi_name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const kpiChartData = getKpiChartData();
  const visibleKpis = getVisibleKpis();
  const hasKpiTrendData = kpiTrendsData?.total_kpis_tracked > 0;

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="text-sm font-medium text-slate-700 mb-2">{formatShortDate(label)}</p>
          {payload.map((entry, index) => (
            <div key={`${entry.dataKey || entry.name || 'k'}-${index}`} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-slate-600">{entry.name}:</span>
              <span className="font-semibold" style={{ color: entry.color }}>
                {entry.value?.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
  
  const __pageTheme = getLightSectionTheme('Navy');
  const trackProgressActions = isSuperAdmin && organizations.length > 0 ? (
    <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}
      className="text-sm rounded-lg px-3 py-1.5 bg-white border border-slate-200 text-slate-800 border-0 focus:ring-2 focus:ring-white/50 min-w-[160px]"
      data-testid="track-progress-org-filter">
      <option value="all">All Organizations</option>
      {organizations.map(o => (<option key={o.id} value={o.id}>{o.name}</option>))}
    </select>
  ) : null;
  const trackProgressStats = [
    { label: 'LEI Imports', value: importHistory.length, testId: 'track-stat-lei-imports' },
    { label: 'KPI Imports', value: kpiHistory.length, testId: 'track-stat-kpi-imports' },
    { label: 'LEI Trends', value: trendsData?.measures?.length || 0, testId: 'track-stat-lei-trends' },
    { label: 'KPI Trends', value: kpiTrendsData?.kpis?.length || 0, testId: 'track-stat-kpi-trends' },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Track Progress"
        description="Import ongoing measurements to track actual progress and compare against your projections. Powers Gap Analysis to compare real results vs. forecasts."
        theme={__pageTheme}
        icon={LineChart}
        actions={trackProgressActions}
        stats={trackProgressStats}
        testId="track-progress-hero"
      />
    )}>
    <div className="min-h-screen bg-slate-50 p-6" data-testid="data-management-page">
      <div className="space-y-6">

        {/* Purpose Clarification */}
        <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-teal-900 font-medium">When to use this page:</p>
                <p className="text-sm text-teal-700 mt-1">
                  Use <strong>Track Progress</strong> to import <em>actual measurements</em> over time. 
                  This data powers the Gap Analysis to compare your real results against projections.
                </p>
                <div className="flex gap-4 mt-3 text-sm">
                  <div className="flex items-center gap-1.5 text-teal-700">
                    <GraduationCap className="h-4 w-4" />
                    <span><strong>LEI Measures:</strong> Monthly/quarterly learning metrics</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-700">
                    <BarChart3 className="h-4 w-4" />
                    <span><strong>Business KPIs:</strong> Actual productivity, quality, etc.</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Tabs for LEI vs KPI Import */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1 bg-slate-100">
            <TabsTrigger 
              value="lei" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <GraduationCap className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">LEI Measures</div>
                <div className="text-xs text-slate-500">Actual learning metrics over time</div>
              </div>
              {importHistory.length > 0 && (
                <Badge variant="secondary" className="ml-2">{importHistory.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="kpi" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <LineChart className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Business KPIs</div>
                <div className="text-xs text-slate-500">Actual business outcomes over time</div>
              </div>
              {kpiHistory.length > 0 && (
                <Badge variant="secondary" className="ml-2">{kpiHistory.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="drivers" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="tab-drivers"
            >
              <Gauge className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Operational Drivers</div>
                <div className="text-xs text-slate-500">Monthly constraint / enabler scores</div>
              </div>
            </TabsTrigger>
          </TabsList>
          
          {/* LEI Import Tab Content */}
          <TabsContent value="lei" className="space-y-6">
        
        {/* Template Explanation Card */}
        <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-teal-800">
              <Info className="h-5 w-5" />
              About the Import Template
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-700">
              The LEI (Learning Experience & Investment) import template allows you to establish baseline measurements 
              for all 15 LEI measures across 4 pillars. Each import is timestamped to enable trend analysis over time.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">P1: Access & Engagement</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Training Scope Index</li>
                  <li>SME Training Scope Index</li>
                  <li>Community Learning Index</li>
                  <li>Voluntary Learning Engagement</li>
                  <li>Technology Utilization Index</li>
                  <li>Organizational Learning Culture</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">P2: Quality & Effectiveness</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Training Performance Index (TPI)</li>
                  <li>Net Promoter Score (NPS)</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">P3: Skills & Capability</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Skills Assessment Performance</li>
                  <li>Skills Coverage Index</li>
                  <li>Certification Depth Index</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">P4: Efficiency & ROI</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Readiness Index</li>
                  <li>Cost Efficiency Index</li>
                  <li>Scheduling Velocity Index</li>
                  <li>KPI Alignment Strength</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <HelpCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">How to use:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Download the template using the button below</li>
                    <li>Fill in the component values for each measure (0-100 scale)</li>
                    <li>Set the measurement date in YYYY-MM-DD format</li>
                    <li>Upload the completed file - invalid rows will be rejected with warnings</li>
                  </ol>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Download & Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-teal-600" />
              Import Data
            </CardTitle>
            <CardDescription>
              Download the template, fill in your data, and upload to import
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                data-testid="download-template-btn"
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
              
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700"
                data-testid="upload-file-btn"
              >
                <Upload className="h-4 w-4" />
                {isUploading ? 'Uploading...' : 'Upload Completed Template'}
              </Button>
            </div>
            
            {/* Upload Result */}
            {uploadResult && (
              <Alert 
                className={uploadResult.success ? 'border-emerald-500 bg-emerald-50' : 'border-rose-500 bg-rose-50'}
                data-testid="upload-result"
              >
                {uploadResult.success ? (
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-rose-600" />
                )}
                <AlertDescription>
                  <div className={uploadResult.success ? 'text-emerald-800' : 'text-rose-800'}>
                    {uploadResult.message || uploadResult.error}
                  </div>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium text-sm text-rose-800">Validation errors (rows rejected):</p>
                      <ul className="list-disc list-inside text-sm mt-1 text-rose-700">
                        {uploadResult.errors.slice(0, 5).map((err, i) => (
                          <li key={`err-${err.row}-${err.measure_id}`}>Row {err.row} ({err.measure_id}): {err.error}</li>
                        ))}
                        {uploadResult.errors.length > 5 && (
                          <li className="text-rose-600 font-medium">...and {uploadResult.errors.length - 5} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* LEI Score Trends Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-600" />
                LEI Score Trends
                {hasTrendData && (
                  <span className="text-sm font-normal text-slate-500">
                    ({trendsData.measure_count} measures tracked)
                  </span>
                )}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hasTrendData ? (
              <div className="space-y-4">
                {/* Quick stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(trendsData.trends || {}).slice(0, 4).map(([id, measure]) => (
                    <div key={id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <p className="text-xs text-slate-500 truncate">{measure.measure_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-lg font-bold text-slate-900">{measure.trend?.latest_score?.toFixed(1) || '—'}</span>
                        {measure.trend?.change !== undefined && (
                          <span className={`text-xs font-medium flex items-center gap-0.5 ${measure.trend.change > 0 ? 'text-emerald-600' : measure.trend.change < 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                            {measure.trend.change > 0 ? <TrendingUp className="h-3 w-3" /> : measure.trend.change < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                            {measure.trend.change > 0 ? '+' : ''}{measure.trend.change_percent?.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full gap-2 text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={() => navigate('/score-trends')} data-testid="lei-view-trends-btn">
                  <BarChart3 className="h-4 w-4" /> View Full LEI Score Trends
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No LEI trend data yet. Import baselines above to see score trends.</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Import History */}
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50 transition-colors rounded-t-lg"
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
          >
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-600" />
                Import History
                <span className="text-sm font-normal text-slate-500">
                  ({sortedBatches.length} {sortedBatches.length === 1 ? 'import' : 'imports'})
                </span>
              </span>
              {isHistoryExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </CardTitle>
          </CardHeader>
          {isHistoryExpanded && (
            <CardContent data-testid="import-history-content">
              {sortedBatches.length > 0 ? (
                <div className="space-y-4">
                  {sortedBatches.map((batch) => (
                    <div 
                      key={batch.import_id} 
                      className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                      data-testid={`import-batch-${batch.import_id}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">
                            {formatDate(batch.import_timestamp)}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {batch.records.length} {batch.records.length === 1 ? 'measure' : 'measures'} imported
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteImport(batch.import_id)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-import-${batch.import_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {batch.records.map((r) => (
                          <span 
                            key={`${r.measure_id}-${r.measurement_date}`} 
                            className="text-xs bg-white px-2.5 py-1.5 rounded border border-slate-200 flex items-center gap-1"
                          >
                            <span className="font-medium text-slate-700">
                              {componentDefs[r.measure_id]?.name || r.measure_id}
                            </span>
                            <span className="text-teal-600 font-semibold">
                              {r.calculated_score?.toFixed(1) || 'N/A'}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileSpreadsheet className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">
                    No imports yet. Download the template and upload your data to get started.
                  </p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
          </TabsContent>
          
          {/* KPI Import Tab Content */}
          <TabsContent value="kpi" className="space-y-6">
            {focusCohortId && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-900" data-testid="import-focus-banner">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="flex items-center gap-2 flex-wrap">
                  Filling the <span className="font-semibold">KPI</span> gap for cohort <span className="font-semibold" data-testid="import-focus-cohort">{focusCohortName || focusCohortId}</span> — use <code className="text-xs bg-amber-100 px-1 rounded">cohort_id = {focusCohortId}</code> in your KPI actuals rows.
                  <button onClick={() => navigate('/data-management?tab=kpi')} className="ml-auto text-xs font-semibold text-amber-800 hover:text-amber-950" data-testid="import-focus-clear">Clear</button>
                </AlertDescription>
              </Alert>
            )}
            {/* KPI Template Explanation Card */}
            <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-rose-800">
                  <Info className="h-5 w-5" />
                  About KPI Actuals Import
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-700">
                  Import actual business KPI data to track real-world outcomes and measure progress against your Outcome Goals.
                  This enables gap analysis between your projected ROI improvements and actual results.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h4 className="font-semibold text-slate-800 mb-2">Performance KPIs</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Productivity per FTE</li>
                      <li>• Quality / Error Reduction</li>
                      <li>• Safety Incident Reduction</li>
                      <li>• Customer Satisfaction (CSAT)</li>
                      <li>• Speed to Market</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h4 className="font-semibold text-slate-800 mb-2">Operational KPIs</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Operational Scalability</li>
                      <li>• Cost Efficiency / Opex</li>
                      <li>• Strategic Initiative Completion</li>
                      <li>• Internal Mobility / Bench Strength</li>
                      <li>• Employee Engagement & Retention</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-rose-100 border border-rose-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="h-5 w-5 text-rose-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-rose-800">
                      <p className="font-medium mb-1">How to use:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Download the KPI template using the button below</li>
                        <li>Fill in actual measured values for each KPI you want to track</li>
                        <li>Set the measurement date in YYYY-MM-DD format</li>
                        <li>Import periodically (monthly/quarterly) to track trends over time</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* KPI Download & Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-rose-600" />
                  Import KPI Actuals
                </CardTitle>
                <CardDescription>
                  Download the template, fill in your actual KPI data, and upload to import
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadKpiTemplate}
                    className="flex items-center gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="download-kpi-template-btn"
                  >
                    <Download className="h-4 w-4" />
                    Download KPI Template
                  </Button>
                  
                  <input value={kpiSource} onChange={(e) => setKpiSource(e.target.value)} placeholder="Source system (e.g. SAP, Salesforce, Workday)" className="h-9 w-64 rounded-md border border-slate-200 bg-white px-2 text-xs" title="Tag this import with the named source system — shown on Performance Health → Evidence" data-testid="kpi-source-input" />
                  <input
                    type="file"
                    ref={kpiFileInputRef}
                    accept=".xlsx,.xls,.csv"
                    onChange={handleKpiFileUpload}
                    className="hidden"
                    data-testid="kpi-file-input"
                  />
                  <Button 
                    onClick={() => kpiFileInputRef.current?.click()}
                    disabled={isKpiUploading}
                    className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700"
                    data-testid="upload-kpi-file-btn"
                  >
                    <Upload className="h-4 w-4" />
                    {isKpiUploading ? 'Uploading...' : 'Upload KPI Data'}
                  </Button>
                </div>
                
                {/* KPI Upload Result */}
                {kpiUploadResult && (
                  <Alert 
                    className={kpiUploadResult.success ? 'border-emerald-500 bg-emerald-50' : 'border-rose-500 bg-rose-50'}
                    data-testid="kpi-upload-result"
                  >
                    {kpiUploadResult.success ? (
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-rose-600" />
                    )}
                    <AlertDescription className={kpiUploadResult.success ? 'text-emerald-800' : 'text-rose-800'}>
                      {kpiUploadResult.success 
                        ? `Successfully imported ${kpiUploadResult.imported_count} KPI records`
                        : `Error: ${kpiUploadResult.error}`}
                      {kpiUploadResult.errors?.length > 0 && (
                        <ul className="mt-2 text-sm">
                          {kpiUploadResult.errors.map((err, i) => (
                            <li key={`kpierr-${i}-${String(err).slice(0,15)}`}>{'\u2022'} {err}</li>
                          ))}
                        </ul>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
            
            {/* KPI Score Trends Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-rose-600" />
                  KPI Score Trends
                  {hasKpiTrendData && (
                    <span className="text-sm font-normal text-slate-500">
                      ({kpiTrendsData?.total_kpis_tracked || 0} KPIs tracked)
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasKpiTrendData ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {Object.entries(kpiTrendsData.by_kpi || {}).slice(0, 4).map(([id, kpi]) => (
                        <div key={id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <p className="text-xs text-slate-500 truncate">{kpi.kpi_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-lg font-bold text-slate-900">{kpi.trend?.latest_value?.toFixed(1) || '—'}</span>
                            {kpi.trend?.change !== undefined && (
                              <span className={`text-xs font-medium flex items-center gap-0.5 ${kpi.trend.change > 0 ? 'text-emerald-600' : kpi.trend.change < 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                                {kpi.trend.change > 0 ? <TrendingUp className="h-3 w-3" /> : kpi.trend.change < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                                {kpi.trend.change > 0 ? '+' : ''}{kpi.trend.change_percent?.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full gap-2 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => navigate('/score-trends?tab=kpi')} data-testid="kpi-view-trends-btn">
                      <BarChart3 className="h-4 w-4" /> View Full KPI Score Trends
                      <ArrowRight className="h-4 w-4 ml-auto" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No KPI trend data yet. Import actuals above to see score trends.</p>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* KPI Import History */}
            <Card>
              <CardHeader>
                <button
                  onClick={() => setIsKpiHistoryExpanded(!isKpiHistoryExpanded)}
                  className="flex items-center justify-between w-full"
                >
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-rose-600" />
                    KPI Import History
                    {kpiHistory.length > 0 && (
                      <Badge variant="outline" className="ml-2">
                        {kpiHistory.length} records
                      </Badge>
                    )}
                  </CardTitle>
                  {isKpiHistoryExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
              </CardHeader>
              {isKpiHistoryExpanded && (
                <CardContent>
                  {kpiHistory.length > 0 ? (
                    <div className="space-y-3">
                      {kpiHistory.map((record, index) => (
                        <div 
                          key={record.period || record.date || `kpi-${index}`}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-rose-100 rounded-lg">
                              <LineChart className="h-4 w-4 text-rose-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-700">{record.kpi_name || record.kpi_id}</p>
                              <p className="text-sm text-slate-500">
                                Measured: {formatDate(record.measurement_date)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-rose-600">
                              {record.primary_value?.toFixed(2)}
                            </p>
                            <p className="text-xs text-slate-400">
                              Imported: {formatDate(record.import_timestamp)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileSpreadsheet className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">
                        No KPI imports yet. Download the template and upload your data to get started.
                      </p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Operational Drivers Import Tab Content */}
          <TabsContent value="drivers" className="space-y-6">
            <DriverImportPanel selectedOrg={selectedOrg} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </PageShell>
  );
}
